<template>
  <div>
    <section class="hero is-primary">
      <div class="container">
        <nav
          class="breadcrumb is-small"
          aria-label="breadcrumbs"
        >
          <ul>
            <li>
              <router-link :to="{ name: 'home' }">
                Home
              </router-link>
            </li>
            <li>
              <router-link :to="{ name: 'songs' }">
                Songs
              </router-link>
            </li>
            <li class="is-active">
              <a
                href="#"
                aria-current="page"
              >{{ song ? song.title : "" }}</a>
            </li>
          </ul>
        </nav>
      </div>
    </section>
    <section class="section">
      <div
        v-if="song"
        class="container"
      >
        <song-edit
          v-if="editMode"
          :initial-song="song"
          @update="updateSelectedSong($event)"
        />
        <div v-else>
          <button
            v-if="wakeLockAvailable()"
            class="button"
            :class="[isWakeLocked ? 'is-active' : '']"
            style="display: inline-block"
            @click="toggleWakeLock()"
          >
            <span class="icon is-small">
              <i
                class="fa-lightbulb"
                :class="[isWakeLocked ? 'fas' : 'far']"
              />
            </span>
          </button>
          <button
            class="button"
            :class="knowledgeClassObject"
            style="display: inline-block"
            @click="changeKnowledge()"
          >
            <span class="icon is-small">
              <i class="fas fa-guitar" />
            </span>
          </button>
          <div style="display: inline-block; margin-bottom: 1.5em">
            <div class="buttons has-addons">
              <button
                class="button"
                :class="[format === 'chords' ? 'is-static' : '']"
                @click="setFormatter('chords')"
              >
                <span class="icon is-small">
                  <i class="fas fa-poll-h" />
                </span>
                <span>Text and chords</span>
              </button>
              <button
                class="button"
                :class="[format === 'text' ? 'is-static' : '']"
                @click="setFormatter('text')"
              >
                <span class="icon is-small">
                  <i class="fas fa-align-left" />
                </span>
                <span>Text only</span>
              </button>
            </div>
          </div>
          <div v-if="song.title">
            <h1
              style="display: inline-block; margin-bottom: 1.5rem; margin-right: 0.5rem"
              class="title"
            >
              {{ song.title }}
            </h1>
            <button
              :class="[
                'button',
                'is-inverted',
                quality === 2
                  ? 'is-success'
                  : quality === 1
                    ? 'is-info'
                    : 'is-danger'
              ]"
              @click="changeQuality()"
            >
              <span class="icon is-small">
                <i :class="qualityClassObject" />
              </span>
            </button>
          </div>
          <h2
            v-if="song.properties && song.properties.couvent_index"
            class="subtitle has-text-grey-light"
            style="margin-top: -1.25rem"
          >
            {{ song.properties.couvent_index }}
          </h2>
          <div
            v-if="song.song"
            class="song-tags"
          >
            <div class="field is-grouped is-grouped-multiline">
              <div
                v-for="tag in song.song.tags"
                :key="tag.id"
                class="control"
              >
                <div class="tags has-addons">
                  <a class="tag is-link">{{ tag.name }}</a>
                </div>
              </div>
            </div>
          </div>
          <div
            class="lyrics"
            :class="'format-' + format"
            v-html="songHTML"
          />
        </div>
        <a
          v-if="editMode"
          class="button is-info"
          @click="remove()"
        >
          <span>Delete</span>
        </a>
      </div>
    </section>
    <a
      class="add button is-large is-warning"
      @click="toggleEdit()"
    >
      <span class="icon is-medium">
        <i
          v-if="editMode"
          class="fas fa-check"
        />
        <i
          v-else
          class="fas fa-pencil-alt"
        />
      </span>
    </a>
  </div>
</template>

<script>
import SongEdit from "../components/SongEdit";
import ChordProParser from "../song_parser";
import HtmlDivFormatter from "../song_formatter";
import { mapActions } from "vuex";

import Kithara from "src/services/kithara";
const kithara = new Kithara();

export default {
  components: {
    SongEdit
  },
  data() {
    return {
      editMode: this.$route.query.edit,
      format: "chords",
      formatter: new HtmlDivFormatter(),
      knowledge: 0,
      quality: 0,
      isWakeLocked: false,
      wakeLock: null
    };
  },
  computed: {
    song: {
      get() {
        return this.$store.getters.selectedSong;
      }
    },
    userData: {
      get() {
        return this.$store.getters.userData;
      }
    },
    songHTML: {
      get() {
        if (this.song && this.song.lyrics) {
          const parser = new ChordProParser();
          const song = parser.parse(this.song.lyrics);
          return this.formatter.format(song);
        } else {
          return "";
        }
      }
    },
    knowledgeClassObject: {
      get() {
        return {
          "knowledge-none": !this.knowledge || this.knowledge === 0,
          "knowledge-bad is-danger": this.knowledge === 1,
          "knowledge-medium is-warning": this.knowledge === 2,
          "knowledge-good is-success": this.knowledge >= 3
        };
      }
    },
    qualityClassObject: {
      get() {
        const quality = this.song.properties ? this.song.properties.quality : 0;
        return {
          fas: true,
          "quality-unknown fa-eye-slash": !quality || quality === 0,
          "quality-need-review fa-eye": quality === 1,
          "quality-certified fa-award": quality === 2
        };
      }
    }
  },
  mounted() {
    this.$store
      .dispatch("selectSong")
      .then(() => {
        this.quality = this.song.properties.quality || 0;
        return kithara.getUserKnowledge(this.userData.id, this.song.id) || 0;
      })
      .then(response => {
        this.knowledge = response.data || 0;
      });

    document.addEventListener(
      "visibilitychange",
      this.handleVisibilityChange,
      false
    );
  },
  beforeDestroy() {
    this.releaseWakeLock();
    document.removeEventListener(
      "visibilitychange",
      this.handleVisibilityChange
    );
  },
  methods: {
    handleVisibilityChange() {
      if (!this.isWakeLocked) return;
      if (document.hidden) {
        this.releaseWakeLock();
        return;
      }
      this.acquireWakeLock();
    },
    remove() {
      if (confirm("Do you really want to delete?")) {
        this.deleteSong().then(() => {
          this.$router.push("/");
        });
      }
    },
    setupQuery() {
      const query = {};
      if (this.editMode) query.edit = this.editMode;
      this.$router.replace({ query });
    },
    toggleEdit() {
      if (this.editMode) this.saveSong();
      this.editMode = !this.editMode;
      this.setupQuery();
    },
    setFormatter(value) {
      this.format = value;
    },
    changeKnowledge() {
      this.knowledge = (this.knowledge + 1) % 4;
      return kithara.setUserKnowledge(
        this.userData.id,
        this.song.id,
        this.knowledge
      );
    },
    changeQuality() {
      this.quality = (this.quality + 1) % 3;
      this.updateSelectedSong({
        path: "properties.quality",
        value: this.quality
      }).then(() => {
        this.saveSong();
      });
    },
    async acquireWakeLock() {
      if (this.wakeLock === null) {
        // Function that attempts to request a wake lock.
        const requestWakeLock = async () => {
          try {
            this.wakeLock = await navigator.wakeLock.request("screen");
            this.wakeLock.addEventListener("release", () => {
              //console.log("Wake Lock was released");
            });
            //console.log("Wake Lock is active");
          } catch (err) {
            //console.error(`${err.name}, ${err.message}`);
          }
        };

        // Request a wake lock…
        await requestWakeLock();
      }
    },
    releaseWakeLock() {
      if (this.wakeLock !== null) {
        this.wakeLock.release();
        this.wakeLock = null;
      }
    },
    wakeLockAvailable() {
      return "wakeLock" in navigator;
    },
    async toggleWakeLock() {
      if (this.isWakeLocked) {
        this.releaseWakeLock();
        this.isWakeLocked = false;
      } else {
        await this.acquireWakeLock();
        this.isWakeLocked = true;
      }
    },
    ...mapActions(["updateSelectedSong", "saveSong", "deleteSong"])
  }
};
</script>

<style lang="scss">
h1.title {
  text-transform: uppercase;
}
.song-tags {
  margin-bottom: 1.5rem;
}
.lyrics {
  white-space: pre;
  h1 {
    /* chordpro titles */
    display: none;
  }
  > .chord-sheet {
    > .paragraph {
      &:not(:empty) {
        margin-bottom: 1.5em;
      }
      &.chorus {
        border-left: solid 3px black;
        padding-left: 1em;
        font-weight: 600;
      }
      &.bridge .lyrics {
        font-style: italic;
      }
      > .row {
        padding-bottom: 0.2em;
        display: flex;
        align-items: flex-end;
        flex-wrap: wrap;
        > .word {
          display: flex;
          align-items: flex-end;
          padding: 0;
          flex-grow: 0;
          > .column {
            padding: 0;
            flex-grow: 0;
          }
        }
      }
    }
  }
}
.chord {
  color: darken($primary, 10%);
  font-weight: bold;
  .lyrics.format-text & {
    display: none;
  }
}
.empty-line {
  display: block;
  height: 1em;
}
.edit {
  @include action-button();
}
</style>



// WEBPACK FOOTER //
// Song.vue?417b6c74